import React from 'react';
import { Star, Clock3, Heart } from 'lucide-react';
import { displayForCategory } from '../../utils/venueCategories';

const FONT = "'Inria Serif', serif";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Turns a stored `venue_categories` array (slugs + custom strings) into
 * the exact subtitle line guests see on the map card — first two
 * categories joined by " · " (mirrors `formatVenueCategoryLabel` in
 * `GuestMapSurface.js`, kept as a local copy so this preview never
 * depends on a live venue id/rating/distance that a draft application
 * doesn't have yet).
 */
export function categoriesToSubtitle(categories) {
  const raw = Array.isArray(categories) ? categories.filter(Boolean) : [];
  if (raw.length === 0) return 'Venue';
  // Show up to 3 categories — the guest card's subtitle line uses ellipsis
  // so long combined labels never overflow past the "Open now" pill.
  return raw.slice(0, 3).map(displayForCategory).join(' · ');
}

function resolvePhotoUrl(photoUrl) {
  if (!photoUrl) return '';
  return photoUrl.startsWith('http') ? photoUrl : `${BACKEND_URL}${photoUrl}`;
}

/**
 * A read-only, presentational replica of the compact guest-map venue
 * card (see `VenueCard` in `GuestMapSurface.js`). Used both in Setup
 * Mode ("this is how you'll look to guests") and in the Venue Card
 * settings section, so owners always see exactly what the map shows —
 * live photo + name + category subtitle + optional description line —
 * without wiring up any of the interactive bits (favorite, reviews,
 * distance) that require a real, approved venue id.
 */
export function VenueCardPreview({ photoUrl, name, categories, description = '', testId = 'venue-card-preview' }) {
  const image = resolvePhotoUrl(photoUrl);
  const subtitle = categoriesToSubtitle(categories);
  const trimmedDescription = (description || '').trim();

  return (
    <div
      data-testid={testId}
      className="premium-card"
      style={{ borderRadius: 26, overflow: 'hidden', width: '100%' }}
    >
      <div style={{ position: 'relative' }}>
        {image ? (
          <img src={image} alt={name || 'Your venue'} style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              background: 'var(--accent-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--accent-purple)',
            }}
          >
            No photo yet
          </div>
        )}
        <div style={{ position: 'absolute', top: 12, left: 12, width: 32, height: 32, borderRadius: '50%', background: 'rgba(18,18,28,0.45)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
          <Heart size={15} color="#FFFFFF" strokeWidth={2} />
        </div>
        <div style={{ position: 'absolute', top: 12, right: 12, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 999, background: 'rgba(18,18,28,0.55)', color: '#FFFFFF', fontFamily: FONT, fontSize: 13, fontWeight: 700, backdropFilter: 'blur(6px)' }}>
          <Star size={13} fill="#F4C54E" color="#F4C54E" />
          New
        </div>
      </div>

      <div style={{ padding: '14px 16px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p data-testid={`${testId}-name`} style={{ fontFamily: FONT, fontSize: 21, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.08 }}>
              {name || 'Your venue'}
            </p>
            <p data-testid={`${testId}-subtitle`} style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: 'var(--text-2)', margin: '6px 0 0', letterSpacing: '0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {subtitle}
            </p>
            {trimmedDescription ? (
              <p data-testid={`${testId}-description`} style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', margin: '3px 0 0', lineHeight: 1.4, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                {trimmedDescription}
              </p>
            ) : null}
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 999, background: 'rgba(17, 163, 82, 0.08)', color: '#138F4E', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Clock3 size={12} />
            Open now
          </div>
        </div>
      </div>
    </div>
  );
}

export default VenueCardPreview;
